import React, {
    FC,
    useCallback,
    useEffect,
    useState,
} from 'react';

import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import Spinner from '../Common/Spin';
import InfiniteScrollTable from './InfiniteScrollTable';
import FileInfoDrawer from './InfiniteScrollTable/FileInfoDrawer';
import TableDropZone from './TableDropZone';
import { useStores } from '../hooks';
import type { SecuredFile } from '@/types/types';
import { AppRoutes } from '@/config/appRoutes';
import appConfig from '@/config/env';
import type { FileActionType } from './FileAction/menuItems';
import { OpenOptions } from '@/config/openOptions';
import { getLinkToViewer, tryCopyToClipboard } from '../utils';
import { getRecipientShareLink } from '@/api/requests';
import styles from './FilesTable.module.scss';
import './antOverride.scss';
import MFAModal from '@/components/Common/MFAModal';
import MFAPasswordModal from '@/components/Common/MFAPasswordModal';
import PermissionDrawer, { PermissionDrawerMode } from './PermissionDrawer';
import Modal, { DynamicStyles, InitModalStyle } from '../Common/Modal';
import { DeleteModal, ShareModal, OpenFileModal } from './Modals';

const nameSpace = 'filesTable';

type DrawerState =
    | { mode: 'closed' }
    | { mode: PermissionDrawerMode; files: SecuredFile[] };

const CLOSED_STATE: DrawerState = { mode: 'closed' };

type ModalType = 'delete' | 'delete_file' | 'openFile' | 'share' | null;

const MODAL_DYNAMIC_STYLES: DynamicStyles = {
    width: true,
    wrapClassName: true,
};

const INIT_STYLE: InitModalStyle = {
    initWidth: 520,
};

const FilesTable: FC = observer(() => {
    const [drawerState, setDrawerState] = useState<DrawerState>(CLOSED_STATE);
    const [fileSelected, setFileSelected] = useState<SecuredFile>(null);
    const [modalType, setModalType] = useState<ModalType>(null);
    /** ShareModal: start on ShareSettings (manage access) vs ShareFile (invite). */
    const [shareModalInitialShareSettings, setShareModalInitialShareSettings] = useState(false);
    const [openApp, setOpenApp] = useState<OpenOptions.googledrive | OpenOptions.office365 | null>(null);

    const navigate = useNavigate();
    const { t } = useTranslation();

    const {
        appStore, filesListStore, filesAccessStore, batchDownloadStore, policyStore,
        sharedUsersStore, authSettingsStore: { API }, userStore,
    } = useStores();
    const { isMyFiles, selectedItem } = filesListStore;
    const {
        setProtectedFileInfo,
    } = filesAccessStore;
    const {
        startSingleDownloadStatusCheck,
        isMFAMounted,
        isMFAPasswordMounted,
    } = batchDownloadStore;
    const { policyList, fetchPolicyList } = policyStore;

    const cf = filesListStore.currentFolder;
    const isWorkspaceFolderRowSelected = !!selectedItem && (
        selectedItem.is_workspace === true
        || (cf?.is_workspace === true && cf.file_id === selectedItem.file_id)
    );
    const hasAccessInfoDrawer = !!(
        (cf?.menu?.edit_shares || userStore.isConfirmedAdmin)
        && isWorkspaceFolderRowSelected
    );

    useEffect(() => {
        fetchPolicyList();
    }, []);

    const findFile = (fileId: string): SecuredFile => {
        if (filesListStore?.currentFolder?.file_id === fileId) {
            return filesListStore?.currentFolder;
        }
        return filesListStore.filesToDisplay.find(({ fid }) => fid === fileId);
    };

    useEffect(() => {
        if (filesListStore.currentFolder?.is_workspace
            && (
                isMyFiles || filesListStore.currentFolder?.menu?.edit_shares || userStore.isConfirmedAdmin
            )) {
            filesListStore.setChosenTableFileId(filesListStore.currentFolder?.file_id);
        }
    }, [filesListStore.currentFolder?.file_id]);

    useEffect(() => {
        if (!appStore.shareSelectedFilesRequested) return;
        appStore.clearShareSelectedFilesRequest();

        const selected = filesListStore.selectedItems as SecuredFile[];
        if (selected.length > 0) {
            setDrawerState({ mode: 'newShare', files: selected });
        }
    }, [appStore.shareSelectedFilesRequested]);

    const handleFileAction = useCallback(async (action: FileActionType, fileId: string): Promise<void> => {
        const file = findFile(fileId);
        const policy = policyList.find((item) => item.id === file.policy_id);
        const protectOnDownloadValue = policy?.settings?.access
            ?.find((setting) => setting.id === 'protectOnDownload')?.value;
        if (!(file?.fid)) {
            message.error(`${nameSpace}.messages.couldNotFind`);
        } else {
            switch (action) {
            case OpenOptions.viewer:
            case OpenOptions.webdav:
                window.open(getLinkToViewer(fileId));
                break;
            case OpenOptions.download:
                if (!file.is_folder) {
                    if (protectOnDownloadValue) {
                        startSingleDownloadStatusCheck(file.file_id, file.filename);
                        setProtectedFileInfo({
                            fileId: file.file_id,
                            filename: file.filename,
                            accessType: action,
                        });
                    } else {
                        batchDownloadStore.setBatchDownload(file.file_id, {
                            id: file.file_id,
                            name: file.filename,
                            status: 'PENDING',
                        });
                        filesAccessStore.tryFetchAccessLink({
                            fileId: file.file_id,
                            filename: file.filename,
                            accessType: action,
                            isProtected: false,
                        });
                    }
                } else {
                    filesAccessStore.tryStartBatchDownload([file.file_id]);
                }
                break;
            case 'audits':
                navigate(`${AppRoutes.auditLogs}?file_id=${fileId}`);
                break;
            case 'manage_access': {
                if (!file.menu?.edit_shares && !userStore.isConfirmedAdmin) break;
                const cf = filesListStore.currentFolder;
                const isWorkspaceRoot = !!(cf?.is_workspace && file?.file_id && cf.file_id === file.file_id);
                if (isWorkspaceRoot) {
                    setFileSelected(file);
                    setShareModalInitialShareSettings(true);
                    setModalType('share');
                } else {
                    setDrawerState({ mode: 'permissions', files: [file] });
                }
                break;
            }
            case 'set_policy':
            case 'set_file_policy':
                if (appConfig.DISABLE_POLICY_CHANGES) {
                    await sharedUsersStore.fetchSharedUsers(fileId);
                    const sharedUsers = sharedUsersStore.getFileSharedUsersList(fileId) || [];
                    if (sharedUsers.length) {
                        break;
                    }
                }

                setDrawerState({ mode: 'permissions', files: [file] });
                break;
            case 'share':
            case 'new_share': {
                const cf = filesListStore.currentFolder;
                const isWorkspaceRoot = !!(cf?.is_workspace && file?.file_id && cf.file_id === file.file_id);
                if (isWorkspaceRoot) {
                    setFileSelected(file);
                    setShareModalInitialShareSettings(false);
                    setModalType('share');
                } else {
                    setDrawerState({ mode: 'newShare', files: [file] });
                }
                break;
            }
            case OpenOptions.googledrive:
            case OpenOptions.office365:
                setFileSelected(file);
                setOpenApp(action);
                setModalType('openFile');
                break;
            case 'copy_link': {
                const link = await getRecipientShareLink(API, file.file_id, userStore.currentUserEmail);
                if (link) {
                    tryCopyToClipboard(link, t('copyLink.link'));
                } else {
                    message.error(t('copyItemToClipboard.error'));
                }
                break;
            }
            case 'delete':
            case 'delete_file':
                setFileSelected(file);
                setModalType(action);
                break;
            default:
                break;
            }
        }
    }, [policyList, filesListStore.currentFolder, filesListStore.filesToDisplay, API, userStore, t]);

    const resetModal = useCallback((): void => {
        setModalType(null);
        setFileSelected(null);
        setShareModalInitialShareSettings(false);
        setOpenApp(null);
    }, []);

    const closeDrawer = useCallback((): void => {
        setDrawerState(CLOSED_STATE);
    }, []);

    const switchToPermissions = useCallback((): void => {
        setDrawerState((prev) => {
            if (prev.mode === 'closed') return prev;
            return { mode: 'permissions', files: prev.files };
        });
    }, []);

    const switchToAddFlow = useCallback((): void => {
        setDrawerState((prev) => {
            if (prev.mode === 'closed') return prev;
            return { mode: 'newShare', files: prev.files };
        });
    }, []);

    const drawerFiles = drawerState.mode !== 'closed' ? drawerState.files : [];
    const drawerMode = drawerState.mode !== 'closed' ? drawerState.mode : undefined;

    const renderFilesTableModal = (): React.ReactElement | null => {
        if (!modalType || !fileSelected) {
            return null;
        }
        switch (modalType) {
        case 'share':
            return (
                <ShareModal
                    key={`${fileSelected.fid}-${shareModalInitialShareSettings ? 'settings' : 'invite'}`}
                    fileSelected={fileSelected}
                    initialShareSettings={shareModalInitialShareSettings}
                    closeModal={resetModal}
                />
            );
        case 'openFile':
            return openApp ? (
                <OpenFileModal
                    fileSelected={fileSelected}
                    app={openApp}
                    closeModal={resetModal}
                />
            ) : null;
        case 'delete':
        case 'delete_file':
            return (
                <DeleteModal
                    fileSelected={fileSelected}
                    closeModal={resetModal}
                />
            );
        default:
            return null;
        }
    };

    return (
        <>
            <div className={styles['files-wrapper']}>
                <Spinner
                    fullHeight
                    screenCentered
                    spinning={filesListStore.isLoading}
                    wrapperClassName="spin-wrapper"
                >
                    <div className={styles['files-table-card']}>
                        <TableDropZone>
                            <InfiniteScrollTable
                                handleFileAction={handleFileAction}
                                hasAccessInfoDrawer={hasAccessInfoDrawer}
                            />
                            {hasAccessInfoDrawer && (
                                <FileInfoDrawer
                                    handleFileAction={handleFileAction}
                                />
                            )}
                        </TableDropZone>
                    </div>
                </Spinner>
            </div>
            {isMFAMounted && <MFAModal />}
            {isMFAPasswordMounted && <MFAPasswordModal />}
            <Modal
                isOpen={!!modalType}
                closeModal={resetModal}
                destroyOnClose
                dynamicStyles={MODAL_DYNAMIC_STYLES}
                initStyle={INIT_STYLE}
            >
                {renderFilesTableModal()}
            </Modal>
            <PermissionDrawer
                files={drawerFiles}
                isOpen={drawerState.mode !== 'closed'}
                onClose={closeDrawer}
                mode={drawerMode}
                onSwitchToPermissions={switchToPermissions}
                onSwitchToAddFlow={switchToAddFlow}
            />
        </>
    );
});

export default FilesTable;
